import {BitmapText, Graphics, IBitmapTextStyle, Matrix} from "pixi.js";
import {Subscription} from "nate-react-api-helpers";
import {DrawFrmThing} from "../../drawfrm/DrawFrmThing";
import {ElevationApp} from "../../ElevationApp";
import {lineBlueHeavy, lineRed} from "../../const";
import {getGlassTexture} from "../frameGrid/item/LiteFace";
import {liteOverlaps} from "../../model/hardware/MdHardwareFootprint";
import {CrosshairRect} from "./HardwareCrosshairThing";

const STROKE = 2;
// the wash over a lite a hardware item reaches into.
const WARNING_ALPHA = 0.3;

const TEXT_STYLE: Partial<IBitmapTextStyle> = {
    fontName: "Arial",
    fontSize: 40,
};

// the glass lites of one door leaf as the hardware views show them: the same glass and
// marks as the door elevation's lites (see ../doorElev/DoorLiteThing), but inert. The
// hardware workspace places hardware around the lites; it does not edit them. A lite a
// placement reaches into draws in the warning colour (see liteOverlaps), and follows the
// placement as it moves.
export class DoorLitesThing extends DrawFrmThing {
    private elev: ElevationApp;
    private side: "active" | "inactive";
    private rect: () => CrosshairRect | null;
    private mirror: () => boolean;

    // the mark label of each lite drawn, by lite id.
    private marks = new Map<string, BitmapText>();
    private placementSubs: Subscription[] = [];

    constructor(props: {
        elev: ElevationApp;
        side: "active" | "inactive";
        // the leaf rect in this Thing's local coords.
        rect: () => CrosshairRect | null;
        // when true, the leaf is shown mirrored left-to-right (the Locations view's back face).
        mirror?: () => boolean;
    }) {
        super();
        this.elev = props.elev;
        this.side = props.side;
        this.rect = props.rect;
        this.mirror = props.mirror ?? (() => false);

        // inert: taps fall through to the leaf.
        this.lines.eventMode = "none";

        const redraw = () => { if(!this.destroyed) this.draw(); };
        this.trackSubscription(this.elev.doorSpec.litesChanged.subscribe(redraw));
        this.trackSubscription(this.elev.hardware.listChanged.subscribe(() => {
            this.watchPlacements();
            redraw();
        }));
        this.watchPlacements();

        this.draw();
    }

    // follow every placement's position so the warning wash tracks it.
    private watchPlacements() {
        for(const sub of this.placementSubs) sub.cancel();
        const redraw = () => { if(!this.destroyed) this.draw(); };
        this.placementSubs = this.elev.hardware.list.flatMap(p => [p.x.subscribe(redraw), p.y.subscribe(redraw)]);
    }

    draw() {
        if(this.destroyed) return;

        const g = this.lines;
        g.clear();

        const r = this.rect();
        const lites = r ? this.elev.doorSpec.litesFor(this.side) : [];

        for(const [id, label] of this.marks) {
            if(lites.some(l => l.id === id)) continue;
            label.destroy();
            this.marks.delete(id);
        }
        if(!r) return;

        const W = r.x1 - r.x0;
        const H = r.y1 - r.y0;
        const mirrored = this.mirror();
        const overlapped = new Set(liteOverlaps(this.elev).map(o => o.lite.id));

        for(const lite of lites) {
            lite.normalize(W, H);
            const w = lite.width.get();
            const h = lite.height.get();
            const left = lite.offsetLeft.get();
            const x = r.x0 + (mirrored ? W - left - w : left);
            const y = r.y0 + lite.offsetTop.get();
            const warning = overlapped.has(lite.id);

            g.lineStyle(STROKE, warning ? lineRed : lineBlueHeavy, 1);
            if(warning) {
                g.beginFill(lineRed, WARNING_ALPHA);
            } else {
                // the same diagonal glass gradient as the door elevation's lites, mapped
                // onto this rect so it fills rather than tiles.
                const texture = getGlassTexture();
                const matrix = new Matrix();
                matrix.scale(w / texture.width, h / texture.height);
                matrix.translate(x, y);
                g.beginTextureFill({texture, matrix});
            }
            g.drawRect(x, y, w, h);
            g.endFill();

            let label = this.marks.get(lite.id);
            if(!label) {
                label = new BitmapText(lite.mark, TEXT_STYLE);
                label.anchor.set(0.5, 0.5);
                label.eventMode = "none";
                this.lines.addChild(label);
                this.marks.set(lite.id, label);
            }
            label.text = lite.mark;
            label.tint = warning ? lineRed : lineBlueHeavy;
            label.position.set(x + w / 2, y + h / 2);
        }
    }

    destroy() {
        if(this.destroyed) return;
        for(const sub of this.placementSubs) sub.cancel();
        this.placementSubs = [];
        super.destroy();
    }
}
